/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { Lock } from 'lucide-react';
import BenefitPills from './BenefitPills';
import amidaMark from '@/assets/amida-mark.png';
import { APP_NAME, PRODUCT_TAGLINE } from '@/lib/branding';

/**
 * Navy gradient background with two radial glows, matching the approved
 * mockup. Applied via an inline style object because the layered
 * radial/linear gradient is design-specific and outside the token set.
 */
const BRAND_BACKGROUND =
  'radial-gradient(circle at 85% 16%, rgba(119,185,255,0.38), transparent 30%),' +
  'radial-gradient(circle at 5% 95%, rgba(77,138,231,0.38), transparent 32%),' +
  'linear-gradient(155deg,#0f1f3d,#0e1c38 45%,#08121f)';

/**
 * Dark navy brand panel shown on large screens only. Carries the Amida mark and
 * the product name, the marketing headline, benefit pills and an SSO footer.
 * Hidden below the `lg` breakpoint so the sign-in column takes the full screen.
 *
 * The mark is the real `amida-mark.png` — the same asset the sidebar uses one
 * screen later. It replaces an inline shield glyph that appeared nowhere else
 * in the product and was not an Amida mark at all.
 *
 * The footer sits at `white/50` rather than the `white/40` it used to: over the
 * bottom of this gradient that measured 3.82:1, and it is 12px body text, so it
 * needed 4.5:1. `white/50` is 5.33:1 and visually near-identical.
 */
export default function BrandPanel() {
  return (
    <aside
      aria-label={`About ${APP_NAME}`}
      className="relative hidden flex-col overflow-hidden p-10 text-white lg:flex xl:p-16"
      style={{ background: BRAND_BACKGROUND }}
    >
      {/* Faint grid texture, faded toward the bottom. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.28] [background-image:linear-gradient(rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:48px_48px] [-webkit-mask-image:linear-gradient(to_bottom,black,transparent_88%)] [mask-image:linear-gradient(to_bottom,black,transparent_88%)]"
      />

      <header className="relative flex items-center gap-3">
        <span className="grid h-11 w-11 flex-none place-items-center rounded-xl bg-white p-1.5">
          {/* Decorative: the wordmark beside it already names the product. */}
          <img src={amidaMark} alt="" className="h-full w-full object-contain" />
        </span>
        <span className="min-w-0">
          <strong className="block break-words text-[17px] font-extrabold tracking-tight text-white">
            {APP_NAME}
          </strong>
          <span className="mt-0.5 block break-words text-[11px] font-semibold uppercase tracking-[0.14em] text-white/50">
            {PRODUCT_TAGLINE}
          </span>
        </span>
      </header>

      <div className="relative flex flex-1 flex-col justify-center py-10">
        <h2 className="max-w-[520px] text-balance text-[clamp(36px,4.5vw,58px)] font-black leading-[1.03] tracking-[-0.045em]">
          Build skills that{' '}
          <em className="bg-[linear-gradient(130deg,#60a5fa,#22d3ee)] bg-clip-text not-italic text-transparent">
            move your mission
          </em>{' '}
          forward.
        </h2>

        <p className="mt-5 max-w-[520px] text-[15px] leading-relaxed text-white/60">
          Amida&apos;s training, in one place. Pick up where you left off, keep your compliance
          current, and collect certificates you can share.
        </p>

        <BenefitPills />
      </div>

      <footer className="relative flex items-center justify-between gap-4 text-xs text-white/50">
        <span>&copy; 2026 Amida Technology Solutions</span>
        <span className="flex items-center gap-1.5">
          <Lock className="h-3.5 w-3.5" aria-hidden="true" />
          Microsoft Entra SSO
        </span>
      </footer>
    </aside>
  );
}
